import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sparkles, ArrowRight, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const apiBase = () => process.env.REACT_APP_BACKEND_URL;
const tok = () => localStorage.getItem('tactical_token');

/**
 * Floating "What's New" banner. Mounted ONCE at the App level so it survives
 * client-side navigation between routes (each route used to remount its own
 * GrowthHubLayout, wiping the banner queue).
 *
 * Behavior:
 *   • Fetches active (un-dismissed) banners on mount AND on every route change.
 *   • Newest one first. "Got it" dismisses permanently (per-user, server-side).
 *   • If the banner has link_path, clicking it dismisses AND navigates.
 *   • Hidden on /login and when the user is not signed in.
 */
const WhatsNewBanner = () => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [dismissing, setDismissing] = useState(false);

  // Re-fetch on login state change AND on every route change. This guarantees
  // the queue self-heals after any navigation, regardless of whether the
  // component instance was remounted or not.
  useEffect(() => {
    if (loading || !isAuthenticated) {
      setItems([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const token = tok();
      if (!token) return;
      try {
        const res = await fetch(`${apiBase()}/api/whats-new/active`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data)) setItems(data);
      } catch (_e) {
        /* silent — banner is non-critical */
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, loading, location.pathname]);

  // Hide on login page and while auth is loading
  if (loading || !isAuthenticated || location.pathname === '/login') return null;

  const current = items[0];
  if (!current) return null;

  const dismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    try {
      await fetch(`${apiBase()}/api/whats-new/${current.id}/dismiss`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${tok()}` },
      });
    } catch (_e) {
      /* still hide locally even if server failed */
    }
    setItems((prev) => prev.slice(1));
    setDismissing(false);
  };

  const goToLink = async () => {
    if (!current.link_path) return;
    await dismiss();
    // Static asset links (the user-manual.html) shouldn't go through React
    // Router — open them in a new tab instead so the user keeps their session.
    if (current.link_path.startsWith('/docs/') || current.link_path.endsWith('.html')) {
      window.open(current.link_path, '_blank', 'noopener');
    } else if (current.link_path.startsWith('http://') || current.link_path.startsWith('https://')) {
      // Absolute URL — open in a new tab (used for the external support portal).
      window.open(current.link_path, '_blank', 'noopener');
    } else {
      navigate(current.link_path);
    }
  };

  return (
    <div
      role="region"
      aria-label="What's New"
      data-testid="whats-new-banner"
      className="fixed bottom-6 right-6 z-50 w-[360px] max-w-[calc(100vw-3rem)] rounded-2xl shadow-2xl bg-white border border-fuchsia-200 overflow-hidden"
    >
      <div className="bg-gradient-to-r from-fuchsia-500 to-cyan-500 px-4 py-2 flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[1.5px] font-bold text-white flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" />
          What's New
        </div>
        <button
          onClick={dismiss}
          disabled={dismissing}
          aria-label="Close"
          data-testid="whats-new-close"
          className="text-white/80 hover:text-white p-0.5 rounded"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-4">
        <div className="text-sm font-bold text-slate-900 leading-snug">{current.title}</div>
        <div className="text-xs text-slate-600 mt-1.5 leading-relaxed whitespace-pre-wrap">{current.body}</div>
        <div className="mt-3 flex items-center justify-between gap-3">
          {current.link_path ? (
            <>
              <button
                onClick={goToLink}
                disabled={dismissing}
                data-testid="whats-new-cta"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-fuchsia-600 text-white hover:bg-fuchsia-700 disabled:opacity-50"
              >
                {current.link_label || 'Check it out'} <ArrowRight className="w-3 h-3" />
              </button>
              <button
                onClick={dismiss}
                disabled={dismissing}
                data-testid="whats-new-dismiss"
                className="text-xs font-semibold text-slate-500 hover:text-slate-700 disabled:opacity-50"
              >
                {current.dismiss_label || 'Got it'}
              </button>
            </>
          ) : (
            // No CTA → make the dismiss button the visual primary action so the user
            // always has an obvious way to acknowledge and close the banner.
            <button
              onClick={dismiss}
              disabled={dismissing}
              data-testid="whats-new-dismiss"
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-fuchsia-600 text-white hover:bg-fuchsia-700 disabled:opacity-50"
            >
              {current.dismiss_label || 'Got it'}
            </button>
          )}
        </div>
        {items.length > 1 && (
          <div className="mt-2 text-[10px] text-slate-400 text-right">
            {items.length - 1} more new {items.length - 1 === 1 ? 'update' : 'updates'} after this
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsNewBanner;
